import { STATUSPAGE } from '@repo/ui';
import { GetAllOrganizationsUserParams } from 'domain/models';
import {
  GetAllCHMetrics,
  GetSummaryProfile,
  GetAllUsersMetrics,
  GetActivityByStatus,
  GetAllActivitiesByTask,
  GetActivitiesInProgressCount,
  GetCompletedAndMissedActivitiesCount,
  GetAllActivitiesByPlace,
  GetAllActivitiesByGroup,
  GetAllActivitiesByStatus,
  GetAllActivitiesByStudent,
  GetAllGroupsUserUseCaseInterface,
  GetAllCoursesUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
  GetAllCurricularUnitsUserUseCaseInterface,
  GetMapActivitiesMarkers,
  GetMapInternshipMarkers,
  GetMapActivityDetails,
} from 'domain/usecases';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadCurricularUnitsUser } from 'presentation/config/store/curricular-units/curricular-units-slice';
import {
  loadActivityByStatus,
  loadingStatusPageActivityByStatus,
  clearActivityByStatus,
} from 'presentation/config/store/dashboard/dashboard-get-activity-by-status';
import { loadDashboardSummary } from 'presentation/config/store/dashboard/dashboard-get-summary.slice';
import {
  loadCHMetrics,
  loadUsersMetrics,
  loadActivitiesByTask,
  loadActivitiesByGroup,
  loadCompletedAndMissedActivitiesCount,
  loadActivitiesInProgressCount,
  loadActivitiesByPlace,
  loadActivitiesByStatus,
  loadActivitiesByStudent,
  loadMapActivitiesMarkers,
  loadMapActivityDetails,
  loadMapInternshipMarkers,
} from 'presentation/config/store/dashboard/dashboard-slice';
import { loadGroupsUser } from 'presentation/config/store/groups/groups-slice';
import {
  setLoadingGroup,
  setLoadingCourse,
  setLoadingSemester,
  setLoadingCurricularUnit,
} from 'presentation/config/store/load-filter/loadingSlice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import { loadSemestersUser } from 'presentation/config/store/semesters/semesters-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

interface IProps {
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllSemestersUser?: GetAllSemestersUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
  getAllCurricularUnitsUser?: GetAllCurricularUnitsUserUseCaseInterface;
}

export const useDashboard = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllSemestersUser,
  getAllOrganizationsUser,
  getAllCurricularUnitsUser,
}: IProps) => {
  const dispatch = useDispatch();
  const { dashboardService } = useService();

  const [loadingSummaryProfile, setLoadingSummaryProfile] =
    useState<STATUSPAGE>();
  const [loadingActivityByStatus, setLoadingActivityByStatus] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [statusPage, setStatusPage] = useState({
    statusCH: STATUSPAGE.DONE,
    statusActivitiesByTask: STATUSPAGE.DONE,
    statusCompletedAndMissedActivitiesCount: STATUSPAGE.DONE,
    statusActivitiesInProgressCount: STATUSPAGE.DONE,
    statusActivitiesByGroup: STATUSPAGE.DONE,
    statusActivitiesByPlace: STATUSPAGE.DONE,
    statusActivitiesByStudent: STATUSPAGE.DONE,
    statusPageActivitiesByStatus: STATUSPAGE.DONE,
    statusPageActivitiesAllByStatus: STATUSPAGE.DONE,
    statusMapActivitiesMarkers: STATUSPAGE.DONE,
    statusMapInternshipMarkers: STATUSPAGE.DONE,
    statusMapActivityDetails: STATUSPAGE.DONE,
  });

  const toggleStatusPage = (key: string, status: STATUSPAGE) => {
    setStatusPage((prevState) => ({
      ...prevState,
      [key]: status,
    }));
  };

  const { organizationsUser } = useAppSelector((state) => state.organizations);
  const { curricularUnitsUser } = useAppSelector(
    (state) => state.curricularUnits,
  );
  const { activityByStatus } = useAppSelector(
    (state) => state.activityByStatus,
  );

  const requestGetAllOrganizationsUser = useCallback(
    (params?: GetAllOrganizationsUserParams) => {
      getAllOrganizationsUser
        .execute(params)
        .then((res) => dispatch(loadOrganizationsUser(res)))
        .catch((err) => console.log(err));
    },
    [],
  );

  const requestGetAllCoursesUser = useCallback((entityId?: string) => {
    dispatch(setLoadingCourse(true));

    getAllCoursesUser
      .execute({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingCourse(false));
      });
  }, []);

  const requestGetAllCurricularUnitsUser = useCallback(
    (params: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      dispatch(setLoadingCurricularUnit(true));

      getAllCurricularUnitsUser
        .execute(params)
        .then((res) => dispatch(loadCurricularUnitsUser(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          dispatch(setLoadingCurricularUnit(false));
        });
    },
    [],
  );

  const requestGetAllSemestersUser = useCallback((entityId: string) => {
    dispatch(setLoadingSemester(true));
    getAllSemestersUser
      .execute({ entityId })
      .then((res) => dispatch(loadSemestersUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingSemester(false));
      });
  }, []);

  const requestGetAllGroupsUser = useCallback(
    (params: GetAllGroupsUserUseCaseInterface.Params) => {
      dispatch(setLoadingGroup(true));

      getAllGroupsUser
        .execute(params)
        .then((res) => dispatch(loadGroupsUser(res)))
        .catch((err) => console.log(err))
        .catch(() => {})
        .finally(() => {
          dispatch(setLoadingGroup(false));
        });
    },
    [],
  );

  const requestGetAllCHMetrics = useCallback(
    (params: GetAllCHMetrics.Params) => {
      toggleStatusPage('statusCH', STATUSPAGE.LOADING);
      dashboardService
        .getAllCHMetrics(params)
        .then((res) => dispatch(loadCHMetrics(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusCH', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetSummaryProfile = useCallback(
    (params: GetSummaryProfile.Params) => {
      setLoadingSummaryProfile(STATUSPAGE.LOADING);
      dashboardService
        .getSummaryProfile(params)
        .then((res) => {
          dispatch(loadDashboardSummary(res));
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setLoadingSummaryProfile(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetCompletedAndMissedActivitiesCount = useCallback(
    (params: GetCompletedAndMissedActivitiesCount.Params) => {
      toggleStatusPage(
        'statusCompletedAndMissedActivitiesCount',
        STATUSPAGE.LOADING,
      );

      dashboardService
        .getCompletedAndMissedActivitiesCount(params)
        .then((res) => dispatch(loadCompletedAndMissedActivitiesCount(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage(
            'statusCompletedAndMissedActivitiesCount',
            STATUSPAGE.DONE,
          );
        });
    },
    [],
  );

  const requestGetActivitiesInProgressCount = useCallback(
    (params: GetActivitiesInProgressCount.Params) => {
      toggleStatusPage('statusActivitiesInProgressCount', STATUSPAGE.LOADING);

      dashboardService
        .getActivitiesInProgressCount(params)
        .then((res) => dispatch(loadActivitiesInProgressCount(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusActivitiesInProgressCount', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllUsersMetrics = useCallback(
    (params?: GetAllUsersMetrics.Params) => {
      dashboardService
        .getAllUsersMetrics(params)
        .then((res) => dispatch(loadUsersMetrics(res)))
        .catch((err) => console.log(err));
    },
    [],
  );

  const requestGetAllActivitiesByPlace = useCallback(
    (params: GetAllActivitiesByPlace.Params) => {
      toggleStatusPage('statusActivitiesByPlace', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByPlace(params)
        .then((res) => dispatch(loadActivitiesByPlace(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusActivitiesByPlace', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByTask = useCallback(
    (params: GetAllActivitiesByTask.Params) => {
      toggleStatusPage('statusActivitiesByTask', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByTask(params)
        .then((res) => dispatch(loadActivitiesByTask(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusActivitiesByTask', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByStudent = useCallback(
    (params: GetAllActivitiesByStudent.Params) => {
      toggleStatusPage('statusActivitiesByStudent', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByStudent(params)
        .then((res) => dispatch(loadActivitiesByStudent(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusActivitiesByStudent', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByGroup = useCallback(
    (params: GetAllActivitiesByGroup.Params) => {
      toggleStatusPage('statusActivitiesByGroup', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByGroup(params)
        .then((res) => dispatch(loadActivitiesByGroup(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusActivitiesByGroup', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetMapActivitiesMarkers = useCallback(
    (params: GetMapActivitiesMarkers.Params) => {
      toggleStatusPage('statusMapActivitiesMarkers', STATUSPAGE.LOADING);

      dashboardService
        .getMapActivitiesMarkers(params)
        .then((res) => dispatch(loadMapActivitiesMarkers(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusMapActivitiesMarkers', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetMapActivityDetails = useCallback(
    (params: GetMapActivityDetails.Params) => {
      toggleStatusPage('statusMapActivityDetails', STATUSPAGE.LOADING);

      dashboardService
        .getMapActivityDetails(params)
        .then((res) => dispatch(loadMapActivityDetails(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusMapActivityDetails', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetMapInternshipMarkers = useCallback(
    (params: GetMapInternshipMarkers.Params) => {
      toggleStatusPage('statusMapInternshipMarkers', STATUSPAGE.LOADING);

      dashboardService
        .getMapInternshipMarkers(params)
        .then((res) => dispatch(loadMapInternshipMarkers(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusMapInternshipMarkers', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByStatus = useCallback(
    (params: GetAllActivitiesByStatus.Params) => {
      toggleStatusPage('statusPageActivitiesAllByStatus', STATUSPAGE.LOADING);
      return dashboardService
        .getAllActivitiesByStatus(params)
        .then((res) => dispatch(loadActivitiesByStatus(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          toggleStatusPage('statusPageActivitiesAllByStatus', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetActivityByStatus = useCallback(
    (params: GetActivityByStatus.Params) => {
      setLoadingActivityByStatus(STATUSPAGE.LOADING);

      dashboardService
        .getActivitiesByStatus(params)
        .then((res: GetActivityByStatus.Result) =>
          dispatch(loadActivityByStatus(res)),
        )
        .catch((err) => console.log(err))
        .finally(() => {
          setLoadingActivityByStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const clearActivityByStatusState = useCallback(() => {
    dispatch(clearActivityByStatus());
  }, []);

  return {
    statusPage,
    activityByStatus,
    organizationsUser,
    curricularUnitsUser,
    loadingSummaryProfile,
    requestGetAllCHMetrics,
    requestGetAllGroupsUser,
    loadingActivityByStatus,
    requestGetAllCoursesUser,
    requestGetSummaryProfile,
    requestGetAllUsersMetrics,
    requestGetActivityByStatus,
    requestGetAllSemestersUser,
    requestGetAllActivitiesByTask,
    requestGetCompletedAndMissedActivitiesCount,
    requestGetActivitiesInProgressCount,
    requestGetAllActivitiesByGroup,
    requestGetAllOrganizationsUser,
    requestGetAllActivitiesByPlace,
    requestGetAllActivitiesByStatus,
    loadingStatusPageActivityByStatus,
    requestGetAllCurricularUnitsUser,
    requestGetAllActivitiesByStudent,
    requestGetMapActivitiesMarkers,
    requestGetMapActivityDetails,
    requestGetMapInternshipMarkers,
    clearActivityByStatusState,
  };
};
